import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { reviewApis } from '../../../gateways/breadstack/reviewApis'
import type { Review, ReviewStatus } from '../../../types/review'

const invalidateList = (queryClient: ReturnType<typeof useQueryClient>, storeId: string) =>
  queryClient.invalidateQueries({ queryKey: ['reviews', storeId] })

export const useReviewDetail = (
  storeId: string,
  id: number | null | undefined,
  productId: number | null | undefined,
) =>
  useQuery({
    queryKey: ['review-detail', storeId, id, productId],
    queryFn: () => reviewApis.getDetail(storeId, id as number, productId as number),
    enabled: Boolean(storeId && id),
  })

export const useUpdateReview = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (review: Partial<Review> & { id: number }) =>
      reviewApis.update(storeId, review),
    onSuccess: () => invalidateList(queryClient, storeId),
  })
}

export const useUpdateReviewStatus = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ review, status }: { review: Review; status: ReviewStatus }) =>
      reviewApis.updateStatus(storeId, review, status),
    onSuccess: () => invalidateList(queryClient, storeId),
  })
}

export const useDeleteReview = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => reviewApis.remove(storeId, id),
    onSuccess: () => invalidateList(queryClient, storeId),
  })
}

export const useBulkUpdateReviewStatus = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ ids, status }: { ids: number[]; status: ReviewStatus }) =>
      reviewApis.bulkUpdate(storeId, ids, status),
    onSuccess: () => invalidateList(queryClient, storeId),
  })
}

export const useBulkDeleteReviews = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (ids: number[]) => reviewApis.bulkDelete(storeId, ids),
    onSuccess: () => invalidateList(queryClient, storeId),
  })
}

export const useSearchProducts = (storeId: string, keyword: string) =>
  useQuery({
    queryKey: ['review-product-search', storeId, keyword],
    queryFn: () => reviewApis.searchProducts(storeId, keyword),
    enabled: Boolean(storeId),
  })

export const useRelatedProduct = (storeId: string, productId: number | null | undefined) =>
  useQuery({
    queryKey: ['review-related-product', storeId, productId],
    queryFn: () => reviewApis.getRelatedProduct(storeId, productId as number),
    enabled: Boolean(storeId && productId),
  })
